import { createContext, useContext, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';

import { useUploadAsset } from 'hooks/useUploadAsset';

interface PrescriptionUploadCtx {
  files: string[];
  fileUploadLoading: boolean;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearFilesList: () => void;
  handleFilesUpload: () => Promise<string[]>;
  urls: string[];
}

const PrescriptionUpload = createContext({} as PrescriptionUploadCtx);

export const PrescriptionUploadProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<string[]>([]);
  const [urls, setUrls] = useState<string[]>([]);
  const { upload } = useUploadAsset();

  const toBase64 = (file: Blob): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => {
        resolve(reader.result as string);
      };
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const dataList = Array.from(e.target.files || []);
    const base64List = await Promise.all(
      dataList.map((file) => toBase64(file)),
    );

    setFiles((current) => [...current, ...base64List]);
  };

  const handleFilesUpload = async (): Promise<string[]> => {
    setLoading(true);

    datadogRum.addTiming('PrescriptionModal/hooks/usePrescriptionUpload');

    const filesURLs = await upload(files);

    filesURLs && setUrls(filesURLs);

    setLoading(false);

    return filesURLs as string[];
  };

  const clearFilesList = () => {
    setFiles([]);
  };

  return (
    <PrescriptionUpload.Provider
      value={{
        files,
        fileUploadLoading: loading,
        handleFileChange,
        clearFilesList,
        handleFilesUpload,
        urls,
      }}
    >
      {children}
    </PrescriptionUpload.Provider>
  );
};

export const usePrescriptionUpload = () => {
  const context = useContext(PrescriptionUpload);

  if (!context) {
    throw new Error(
      'usePrescriptionUpload hook must be used under PrescriptionUploadProvider',
    );
  }

  return context;
};
